import { type ValueFormatterParams } from "ag-grid-enterprise";

import { type BuilderInfo, CurrencyLocale, TimeLocale } from "helpers/AppProvider.types";

import { formatCurrency, formatNumber, getAccuracy } from "utilities/number/number";

import { formatDate } from "commonComponents/utilities/DateDisplay/DateDisplayDate.utilities";
import { type INumberResponse } from "commonComponents/utilities/Grid/common/columnImplementations/columnCurrency";

/**
 * Grid context interface for accessing builder info in value formatters.
 * This is passed via the `context` prop on AgGridReact.
 */
interface IGridContext {
    builderInfo?: BuilderInfo;
}

/**
 * Safely extracts the currency locale from grid context.
 */
function getCurrencyLocale(context: unknown): CurrencyLocale {
    const gridContext = context as IGridContext | undefined;
    return gridContext?.builderInfo?.locale.currencyLocale ?? CurrencyLocale.default;
}

/**
 * Safely extracts the time locale from grid context.
 */
function getTimeLocale(context: unknown): TimeLocale {
    const gridContext = context as IGridContext | undefined;
    return gridContext?.builderInfo?.locale.timeLocale ?? TimeLocale.default;
}

/**
 * Safely extracts the timeCardUseHoursMinutesFormat flag from grid context.
 */
function getTimeCardUseHoursMinutesFormat(context: unknown): boolean {
    const gridContext = context as IGridContext | undefined;
    return gridContext?.builderInfo?.configFlags?.timeCardUseHoursMinutesFormat ?? false;
}

/**
 * Currency value formatter.
 * Automatically uses builder's locale from grid context when available.
 * Used by standardColumnImplementations for Excel export support.
 */
export function currencyValueFormatter(params: ValueFormatterParams): string {
    const currencyLocale = getCurrencyLocale(params.context);
    const value = params.value as number | null | undefined;
    if (value != null) {
        return formatCurrency(value, currencyLocale, {
            decimalScale: 2,
            fixedDecimalScale: true,
        });
    }
    return "";
}

/**
 * Percentage value formatter.
 * Automatically uses builder's locale from grid context when available.
 * Used by standardColumnImplementations for Excel export support.
 */
export function percentageValueFormatter(params: ValueFormatterParams): string {
    const currencyLocale = getCurrencyLocale(params.context);
    const value = params.value as number | null | undefined;
    if (value != null) {
        return formatNumber(value, currencyLocale, {
            decimalScale: 2,
            fixedDecimalScale: true,
            suffix: "%",
        });
    }
    return "";
}

/**
 * Number value formatter.
 * Handles INumberResponse objects with dynamic decimal scale.
 * Automatically uses builder's locale from grid context when available.
 * Used by standardColumnImplementations for Excel export support.
 */
export function numberValueFormatter(
    params: ValueFormatterParams<unknown, INumberResponse | undefined>,
): string {
    const currencyLocale = getCurrencyLocale(params.context);
    const value = params.value?.value;
    if (value != null) {
        const decimalScale = Math.max(0, getAccuracy(value));
        return formatNumber(value, currencyLocale, {
            decimalScale,
            fixedDecimalScale: true,
        });
    }
    return "";
}

/**
 * Date value formatter.
 * Formats dates using the standard date format (e.g., "Dec 19, 2024").
 * Automatically uses builder's locale from grid context when available.
 * Used by standardColumnImplementations for Excel export support.
 */
export function dateValueFormatter(params: ValueFormatterParams): string {
    const timeLocale = getTimeLocale(params.context);
    const value = params.value as string | number | Date | null | undefined;
    if (value != null) {
        const date = value instanceof Date ? value : new Date(value);
        if (!isNaN(date.getTime())) {
            return formatDate(date, timeLocale, {
                showDate: true,
                showTime: false,
                showDayOfWeek: false,
            });
        }
    }
    return "";
}

/**
 * Date + time value formatter.
 * Same as dateValueFormatter but also includes the time (e.g. "Dec 19, 2024 3:04 PM").
 * Automatically uses builder's locale from grid context when available.
 * Used by standardColumnImplementations for Excel export support.
 */
export function dateTimeValueFormatter(params: ValueFormatterParams): string {
    const timeLocale = getTimeLocale(params.context);
    const value = params.value as string | number | Date | null | undefined;
    if (value != null) {
        const date = value instanceof Date ? value : new Date(value);
        if (!isNaN(date.getTime())) {
            return formatDate(date, timeLocale, {
                showDate: true,
                showTime: true,
                showDayOfWeek: false,
            });
        }
    }
    return "";
}

/**
 * Default checkbox value formatter.
 * Formats boolean values as "Yes" or "No".
 * Used by standardColumnImplementations for basic Excel export support.
 */
export function checkboxValueFormatter(params: ValueFormatterParams): string {
    return params.value ? "Yes" : "No";
}

/**
 * User data shape for value formatter.
 */
interface IUserValue {
    displayName?: string;
    name?: string;
}

/**
 * Default user value formatter.
 * Extracts and returns the display name from user data.
 * Used by standardColumnImplementations for basic Excel export support.
 */
export function userValueFormatter(params: ValueFormatterParams): string {
    // User columns typically use custom getDisplayName functions,
    // so we return empty by default - grids should override with their own
    const value = params.value as IUserValue | null | undefined;
    return value?.displayName ?? value?.name ?? "";
}

/**
 * Decimal value formatter.
 * Automatically uses builder's locale from grid context when available.
 * Used for numeric values that need locale-specific decimal/grouping separators.
 */
export function decimalValueFormatter(params: ValueFormatterParams): string {
    const currencyLocale = getCurrencyLocale(params.context);
    const value = params.value as number | null | undefined;
    if (value != null) {
        return formatNumber(value, currencyLocale, {
            decimalScale: 2,
            fixedDecimalScale: true,
        });
    }
    return "";
}

/**
 * Time value formatter for hours.
 * Formats time values based on the builder's timeCardUseHoursMinutesFormat config:
 * - When true: Displays as HH:MM format (e.g., "2:30")
 * - When false: Displays as decimal format with locale-specific separator (e.g., "2.50")
 *
 * Expects the value to be in decimal hours (e.g., 2.5 hours).
 */
export function timeValueFormatter(params: ValueFormatterParams): string {
    const useHoursMinutesFormat = getTimeCardUseHoursMinutesFormat(params.context);
    const currencyLocale = getCurrencyLocale(params.context);
    const value = params.value as number | null | undefined;

    if (value == null) {
        return "";
    }

    if (useHoursMinutesFormat) {
        const hours = Math.floor(value);
        const minutes = Math.round((value - hours) * 60);
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    } else {
        return formatNumber(value, currencyLocale, {
            decimalScale: 2,
            fixedDecimalScale: true,
        });
    }
}
